import React, { useState, useEffect } from 'react';
import { generateKeys, getKeys, exportKeys, importKeys, getDB } from '../../utils/crypto';
import QRCodeGenerator from '../../components/QRCodeGenerator';

const AdminPage: React.FC = () => {
  const [hasKeys, setHasKeys] = useState<boolean>(false);
  const [exportedKeys, setExportedKeys] = useState<string | null>(null);
  const [importValue, setImportValue] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'keys' | 'qrcodes'>('keys');

  // Check if keys exist on component mount
  useEffect(() => {
    checkKeys();
  }, []);

  // Check for existing keys
  const checkKeys = async () => {
    try {
      // First ensure the database is initialized
      await getDB();
      
      // Then check for keys
      const keys = await getKeys();
      setHasKeys(!!keys);
    } catch (error) {
      console.error('Error checking keys:', error);
      setMessage({ type: 'error', text: 'Fehler beim Überprüfen der Schlüssel.' });
    }
  };
  
  // Load keys from IndexedDB and expose them to the UI
  const handleLoadKeysFromDB = async () => {
    try {
      // First ensure the database is initialized
      await getDB();
      
      // Then try to load keys from IndexedDB
      const keyData = await exportKeys();
      
      if (keyData) {
        setExportedKeys(keyData);
        setHasKeys(true);
        setMessage({ type: 'success', text: 'Schlüssel erfolgreich aus der Datenbank geladen!' });
      } else {
        setMessage({ type: 'error', text: 'Keine Schlüssel in der Datenbank gefunden.' });
      }
    } catch (error) {
      console.error('Error loading keys from DB:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Schlüssel aus der Datenbank.' });
    }
  };

  // Generate new keys
  const handleGenerateKeys = async () => {
    try {
      // First ensure the database is initialized
      await getDB();
      
      // Then generate keys
      await generateKeys();
      setHasKeys(true);
      setMessage({ type: 'success', text: 'Schlüssel erfolgreich generiert!' });
    } catch (error) {
      console.error('Error generating keys:', error);
      // More detailed error message
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler';
      setMessage({ type: 'error', text: `Fehler bei der Schlüsselgenerierung: ${errorMessage}` });
    }
  };

  // Export keys to text
  const handleExportKeys = async () => {
    try {
      const keyData = await exportKeys();
      setExportedKeys(keyData);
      setMessage({ type: 'success', text: 'Schlüssel erfolgreich exportiert!' });
    } catch (error) {
      console.error('Error exporting keys:', error);
      setMessage({ type: 'error', text: 'Fehler beim Exportieren der Schlüssel.' });
    }
  };

  // Import keys from text
  const handleImportKeys = async () => {
    try {
      if (!importValue.trim()) {
        setMessage({ type: 'error', text: 'Bitte geben Sie einen Schlüssel ein.' });
        return;
      }
      
      const success = await importKeys(importValue);
      
      if (success) {
        setHasKeys(true);
        setImportValue('');
        setMessage({ type: 'success', text: 'Schlüssel erfolgreich importiert!' });
      } else {
        setMessage({ type: 'error', text: 'Ungültiger Schlüssel.' });
      }
    } catch (error) {
      console.error('Error importing keys:', error);
      setMessage({ type: 'error', text: 'Fehler beim Importieren der Schlüssel.' });
    }
  };

  // Copy exported keys to clipboard
  const copyToClipboard = async () => {
    if (exportedKeys) {
      try {
        await navigator.clipboard.writeText(exportedKeys);
        setMessage({ type: 'success', text: 'In die Zwischenablage kopiert!' });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        setMessage({ type: 'error', text: 'Fehler beim Kopieren in die Zwischenablage.' });
      }
    }
  };

  return (
    <div className="admin-page">
      <h1>Admin-Bereich</h1>
      
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'keys' ? 'active' : ''}`}
          onClick={() => setActiveTab('keys')}
        >
          Schlüsselverwaltung
        </button>
        <button 
          className={`tab ${activeTab === 'qrcodes' ? 'active' : ''}`}
          onClick={() => setActiveTab('qrcodes')}
          disabled={!hasKeys}
        >
          QR-Code Generator
        </button>
      </div>
      
      {message && (
        <div className={`message ${message.type}`}>
          <span className="message-icon">
            {message.type === 'success' ? '✓' : '✗'}
          </span>
          {message.text}
          <button className="close-button" onClick={() => setMessage(null)}>×</button>
        </div>
      )}
      
      {activeTab === 'keys' ? (
        <div className="key-management">
          <div className="key-status">
            <h2>Schlüsselstatus</h2>
            <p>
              Status: {hasKeys ? (
                <span className="status-indicator has-keys">Schlüssel vorhanden</span>
              ) : (
                <span className="status-indicator no-keys">Keine Schlüssel</span>
              )}
            </p>
            
            <div className="key-actions">
              {!hasKeys && (
                <button className="primary-button" onClick={handleGenerateKeys}>
                  Neue EdDSA-Schlüssel generieren
                </button>
              )}
              
              <button 
                className="secondary-button" 
                onClick={handleLoadKeysFromDB}
                title="Lädt vorhandene Schlüssel aus der lokalen Datenbank"
              >
                Gespeicherte Schlüssel laden
              </button>
            </div>
          </div>
          
          {hasKeys && (
            <>
              <div className="export-keys">
                <h2>Schlüssel exportieren</h2>
                <p>Exportieren Sie Ihre Schlüssel zur Sicherung oder Übertragung:</p>
                
                <div className="button-group">
                  <button className="secondary-button" onClick={handleExportKeys}>
                    Schlüssel aus DB exportieren
                  </button>
                </div>
                
                {exportedKeys && (
                  <div className="exported-keys-container">
                    <div className="export-status">Schlüsselinhalt:</div>
                    <textarea
                      className="exported-keys"
                      value={exportedKeys}
                      readOnly
                    />
                    <div className="button-group">
                      <button className="icon-button copy-button" onClick={copyToClipboard}>
                        In Zwischenablage kopieren
                      </button>
                      <button 
                        className="icon-button download-button" 
                        onClick={() => {
                          const blob = new Blob([exportedKeys], { type: 'application/json' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `raetseljagd-keys-${new Date().toISOString().slice(0, 10)}.json`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                        }}
                      >
                        Als Datei herunterladen
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="import-keys">
                <h2>Schlüssel importieren</h2>
                <p>Importieren Sie bestehende Schlüssel:</p>
                <textarea
                  className="import-textarea"
                  value={importValue}
                  onChange={(e) => setImportValue(e.target.value)}
                  placeholder="Fügen Sie hier den exportierten Schlüssel ein..."
                />
                <button className="secondary-button" onClick={handleImportKeys}>
                  Schlüssel importieren
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <QRCodeGenerator />
      )}
    </div>
  );
};

export default AdminPage;